/**
 * CompingEngine.js
 * Generates rhythmic chording patterns that lock to a reference groove (Kick/Snare).
 */

import { SCALE_INTERVALS, NOTES, normalizeKey } from '../../data/theoryConstants.js';

/**
 * Generates rhythm events for a chord instrument (Piano, Guitar) that align with the kick pattern.
 * @param {Object} chord - The chord object { notes, rootPitchClass, duration... }
 * @param {Array} kickPattern - Array of kick start times (relative to bar start). e.g. [0, 0.75, 2.0]
 * @param {string} strategy - 'lock', 'counter', 'sustain'
 * @param {Object} context - { barStart, barIndex, density }
 */
export const generateLockedGroove = (chord, kickPattern, strategy = 'lock', context) => {
    const events = [];
    // Allow empty kick pattern if explicit comping pattern is requested
    const pattern = (context.traits && context.traits.compingPattern) || null;

    const barStart = context.barStart || 0;

    // Backup Density Check: If global density is extremely low, reduce note count.
    const density = context.density !== undefined ? context.density : 0.5;
    const sparsify = (prob) => {
        // If density is low, reduce probability.
        // e.g. density 0.1 -> prob * 0.2
        // density 0.5 -> prob * 1.0
        const scaler = Math.min(1.0, density * 2.0);
        return prob * scaler;
    };

    // 0. EXPLICIT PATTERN STRATEGY (e.g. Charleston)
    if (pattern === 'charleston') {
        // Charleston: Dotted quarter + Eighth (Beat 1 and Beat 2&)
        // 1 . . . & . . .
        events.push({ s: barStart, d: chord.rootPitchClass, notes: chord.notes, len: 1.0, vel: 0.9, articulation: 'marcato' });
        events.push({ s: barStart + 1.5, d: chord.rootPitchClass, notes: chord.notes, len: 0.5, vel: 0.8, articulation: 'staccato' });
        return events;
    }
    else if (pattern === 'four_to_floor') {
        // Quarter notes
        for (let i=0; i<4; i++) {
            events.push({ s: barStart + i, d: chord.rootPitchClass, notes: chord.notes, len: 0.5, vel: 0.85, articulation: 'marcato' });
        }
        return events;
    }
    else if (pattern === 'waltz_comping') {
        // Oom-pah-pah: Bass on 1 (handled by BassEngine usually, but we can do chords on 2, 3)
        // Piano LH plays root on 1? Let's assume this comping engine does the "Pah-Pah" chords.
        // Beats 2 and 3 (index 1 and 2)
        events.push({ s: barStart + 1, d: chord.rootPitchClass, notes: chord.notes, len: 0.5, vel: 0.7, articulation: 'staccato' });
        events.push({ s: barStart + 2, d: chord.rootPitchClass, notes: chord.notes, len: 0.5, vel: 0.7, articulation: 'staccato' });
        return events;
    }

    // Allow sustain strategy to proceed without kick pattern
    if ((!kickPattern || kickPattern.length === 0) && strategy !== 'sustain') return events;

    // Sort kick pattern
    const sortedKicks = kickPattern ? [...kickPattern].sort((a,b) => a - b) : [];

    // 1. LOCK STRATEGY: Play exactly on Kick
    if (strategy === 'lock') {
        sortedKicks.forEach(k => {
            // Apply sparsity check for 'Lock' strategy too?
            // Usually Lock implies tight coupling, but if density is 0.1, we shouldn't play 16 notes.
            // Let's drop random notes if density is low.
            if (Math.random() < sparsify(1.0)) {
                events.push({
                    s: barStart + k,
                    d: chord.rootPitchClass,
                    notes: chord.notes || [],
                    len: 0.2,
                    vel: 0.8,
                    articulation: 'staccato'
                });
            }
        });
    }

    // 2. COUNTER STRATEGY: Interlocking Rhythms
    // Play on weak beats or gaps to complement the kick.
    else if (strategy === 'counter') {
        // Interlock Logic:
        // - Priority 1: Play on the "and" (8th offbeats) if Kick didn't play.
        // - Priority 2: Play "Response" on the next 16th if Kick played on a strong beat (Call -> Response).

        const kickTimes = new Set(sortedKicks.map(k => Math.round(k * 4) / 4)); // Quantize to 16ths

        for (let i = 0; i < 16; i++) { // 16th note grid
            const time = i * 0.25;

            // Check if Kick played here
            if (kickTimes.has(time)) {
                // Kick played.
                // Maybe play a response on next 16th?
                const nextTime = time + 0.25;
                if (!kickTimes.has(nextTime) && (i % 4 === 0)) { // If kick was on Downbeat, response on 'e'
                     // Dynamic Probability based on Complexity
                     // Use local density if provided, else vector
                     const localDensity = context.density ?? context.config?.vectors?.melodicDensity ?? 0.5;
                     const responseChance = sparsify(0.2 + (localDensity * 0.4)); // Apply sparsity scaler

                     if (Math.random() < responseChance) {
                         events.push({
                            s: barStart + nextTime,
                            d: chord.rootPitchClass,
                            notes: chord.notes,
                            len: 0.15,
                            vel: 0.5, // Ghost
                            articulation: 'staccato'
                        });
                     }
                }
                continue;
            }

            // Kick did NOT play here.
            // Check for Offbeat (The "&")
            if (i % 4 === 2) { // 2, 6, 10, 14 (0.5, 1.5, 2.5, 3.5)
                // Classic Reggae/Ska/Funk offbeat chop
                events.push({
                    s: barStart + time,
                    d: chord.rootPitchClass,
                    notes: chord.notes,
                    len: 0.2,
                    vel: 0.8,
                    articulation: 'staccato'
                });
            }
            // Check for "a" (last 16th) syncopation
            else if (i % 4 === 3) {
                // Play if the upcoming downbeat has a kick (Anticipation)
                const nextDownbeat = Math.ceil(time);

                // Dynamic Anticipation Chance
                const tension = (context.tension || 0.5);
                const anticipateChance = 0.3 + (tension * 0.5); // Higher tension -> More anticipation

                if (kickTimes.has(nextDownbeat) && Math.random() < anticipateChance) {
                     events.push({
                        s: barStart + time,
                        d: chord.rootPitchClass,
                        notes: chord.notes,
                        len: 0.2, // Tie over?
                        vel: 0.7,
                        articulation: 'accent'
                    });
                }
            }
        }
    }

    // 3. SUSTAIN / PAD (Active Accompaniment - Internal Motion)
    else {
        const sortedNotes = (chord.notes || []).sort((a,b) => a-b);
        const complexity = context.config?.vectors?.harmonicComplexity ?? 0.5;

        // Only activate Internal Motion if complexity is sufficient and we have enough voices
        const allowMotion = (complexity > 0.3) && (sortedNotes.length >= 3);

        if (allowMotion) {
            // Split Bass and Inner Voices
            const bass = sortedNotes[0];
            const inner = sortedNotes.slice(1);

            // 1. Sustain Bass (Anchor)
            events.push({
                s: barStart,
                d: bass,
                len: chord.duration,
                vel: 0.65,
                articulation: 'legato',
                role: 'pad_bass'
            });

            // 2. Process Inner Voices
            // Strategy: Select ONE voice to move (Middle or Top) to maintain clarity.
            const prng = context.random;
            const roll = prng ? prng.next() : Math.random();
            const shouldMoveAny = roll < (complexity * 0.9); // 90% chance max

            let targetIdx = -1;
            if (shouldMoveAny && inner.length > 0) {
                // Prefer top voice or middle
                // 50/50 split
                const pickRoll = prng ? prng.next() : Math.random();
                if (pickRoll > 0.5) {
                    targetIdx = inner.length - 1; // Top
                } else {
                    targetIdx = Math.floor(inner.length / 2); // Middle
                }
            }

            inner.forEach((note, idx) => {
                if (idx === targetIdx) {
                    const dur = (typeof chord.duration === 'number' && !isNaN(chord.duration)) ? chord.duration : 4.0;
                    const movePoint = dur / 2; // Move at half-bar (Subdivision)

                    // Segment 1: Sustain
                    events.push({
                        s: barStart,
                        d: note,
                        len: movePoint,
                        vel: 0.6,
                        articulation: 'legato',
                        role: 'pad_inner'
                    });

                    // Segment 2: Move to Neighbor
                    // Calculate Diatonic Neighbor
                    const scaleType = chord.scale || context.scaleType || 'major';
                    const rootKey = chord.key || context.rootKey || 'C';

                    // Resolve Scale Intervals
                    const intervals = SCALE_INTERVALS[scaleType] || SCALE_INTERVALS.major;
                    const rootIdx = NOTES.indexOf(normalizeKey(rootKey));

                    // Current Step
                    // Naive Neighbor: +/- 1 or 2 semitones
                    // Direction based on Tension: High Tension -> Move Up, Low -> Move Down
                    const tension = context.tension || 0.5;
                    const dir = (tension > 0.6) ? 1 : -1;

                    // Find nearest scale tone in direction
                    let neighbor = note + dir;
                    let safety = 0;
                    while (safety < 4) { // Search range
                        const pc = neighbor % 12;
                        // Check if pc is in scale
                        const isScale = intervals.some(iv => (rootIdx + iv) % 12 === pc);
                        if (isScale) break;
                        neighbor += dir;
                        safety++;
                    }
                    if (safety >= 4) neighbor = note + (dir * 2); // Fallback to whole step

                    events.push({
                        s: barStart + movePoint,
                        d: neighbor,
                        len: dur - movePoint,
                        vel: 0.6,
                        articulation: 'legato',
                        role: 'pad_inner_move'
                    });

                } else {
                    // Static Inner Voice
                    events.push({
                        s: barStart,
                        d: note,
                        len: chord.duration,
                        vel: 0.6,
                        articulation: 'legato',
                        role: 'pad_inner'
                    });
                }
            });

        } else {
            // Legacy Static Block
            events.push({
                s: barStart,
                d: chord.rootPitchClass,
                notes: chord.notes,
                len: chord.duration,
                vel: 0.6,
                articulation: 'legato'
            });
        }
    }

    return events;
};
